import { Document, Font, PDFDownloadLink, Page, StyleSheet, Text, View, pdf } from '@react-pdf/renderer';
import React from 'react';
import { Button } from 'reactstrap';
import useAuth from '../../hooks/useAuth';
import { MODULES_ENUM, PERMISSIONS_ENUM, USER_TYPES_ENUM } from "../../utility/constants";
import { formatDate } from '../../utility/formatDate';
import getPaymentTerm from "../../utility/getPaymentTerm";

const getTaxRows = (data) => {
  const { totalCGST, totalSGST, totalIGST, totalAmount, grandTotal } = data.orderInfo;

  const rows = [];

  rows.push({ label: 'Sub Total:', value: totalAmount });
  if (totalCGST > 0) rows.push({ label: 'CGST:', value: totalCGST });
  if (totalSGST > 0) rows.push({ label: 'SGST:', value: totalSGST });
  if (totalIGST > 0) rows.push({ label: 'IGST:', value: totalIGST });

  rows.push({ label: 'Total:', value: grandTotal });

  return rows;
};
Font.register({
  family: 'Ubuntu',
  fonts: [
    { src: 'https://cdn.jsdelivr.net/fontsource/fonts/ubuntu@latest/latin-400-normal.ttf', fontWeight: 400 },
    { src: 'https://cdn.jsdelivr.net/fontsource/fonts/ubuntu@latest/latin-700-normal.ttf', fontWeight: 700 },
  ],
});

// Define styles for the PDF
const styles = StyleSheet.create({

  page: {
    padding: 30,
    fontSize: 8,
    fontFamily: 'Ubuntu',
    position: "relative",
  },
  watermarkWrapper: {
    position: 'absolute',
    top: '50%',
    left: 0,
    right: 0,
    width: '100%',
    marginTop: -40,
  },
  watermarkContainer: {
    opacity: 0.08,
    transform: 'rotate(-45deg)',
    width: '100%',
    textAlign: 'center',
  },
  watermarkText: {
    fontSize: 60,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  pageBorder: {
    position: 'absolute',
    top: 30,
    right: 30,
    bottom: 30,
    left: 30,
    borderWidth: 1,
    borderColor: 'gray'
  },
  status: {
    borderStyle: 'solid',
    borderWidth: 1,
    borderRadius: 50,
    padding: "2px 6px",
    textTransform: "capitalize",
    fontSize: 8,
    marginTop: 4
  },
  title: {
    fontSize: 12,
    fontWeight: 'bold'
  },
  section: {
    marginBottom: 10,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: "flex-start",
    gap: 4,
  },
  addressSection: {
    marginBottom: 10,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: "flex-start",
    gap: 12
  },
  address: {
    width: '49%'
  },
  table: {
    display: 'table',
    width: 'auto',
    marginBottom: 10,
  },
  tableRow: {
    flexDirection: 'row',
  },
  tableCol: {
    width: 'auto',
    flex: 1,
    borderStyle: 'solid',
    borderWidth: 1,
    paddingHorizontal: 5,
    paddingVertical: 3,
    borderColor: 'gray'
  },
  hnsCol: {
    width: 50,
    flex: 1,
    borderStyle: 'solid',
    borderWidth: 1,
    paddingHorizontal: 5,
    paddingVertical: 3,
    borderColor: 'gray'
  },
  firstCol: {
    width: 10,
    borderStyle: 'solid',
    flex: 1,
    borderWidth: 1,
    paddingHorizontal: 5,
    paddingVertical: 3,
    borderColor: 'gray'
  },
  descCol: {
    width: 180,
    borderStyle: 'solid',
    borderWidth: 1,
    paddingHorizontal: 5,
    paddingVertical: 3,
    borderColor: 'gray'
  },
  tableCell: {
    fontSize: 8,
    textAlign: 'center',
  },
  header: {
    fontSize: 8,
    fontWeight: 700,
    marginBottom: 4
  },
  info: {
    fontSize: 8,
  },
  terms: {
    width: '65%',
    padding: 5,
  },
  note: {
    fontSize: 7,
     marginTop: 30,
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: 'black',
    marginVertical: 10,
    borderColor: 'gray'
  },
  infoRow: {
    flexDirection: "row",
    marginBottom: 4,
    paddingHorizontal: 10,
  },

  label: {
    width: 80,
    fontSize: 8,
    fontWeight: "bold",
  },

  value: {
    flex: 1,
    fontSize: 8,
  },
});

const getAddressValue = (branch) => {
  if (!branch?.address) return '';
  return <>
    {branch?.address.addressLine1},<br />
    {branch?.address.addressLine2 || ''},<br />
    {branch?.address.state}&nbsp;{branch?.address.city}&nbsp;-&nbsp;{branch?.address.pincode}.
  </>;
};
 const getOfficeAddress = (branch) => {
    if (!branch?.address) return '';
    return (
      <>
        {branch?.associatedWarehouse?.address?.addressLine1||"No. 1496, Opp. Park Square Apartment,"}<br />
        {branch?.associatedWarehouse?.address?.addressLine2 ||"19th Main, 1st Sector, HSR Layout"},<br />
        {branch?.associatedWarehouse?.address?.state||"Karnataka"}&nbsp;{branch?.associatedWarehouse?.address?.city||"Bengaluru"}&nbsp;-&nbsp;
        {branch?.associatedWarehouse?.address?.pincode||"560102"}.
      </>
    );
  };

// Function to display table with pagination
const TableWithPagination = ({ data, showAmount }) => {
  return (
    <View>
      {/* Table Header */}
      <View style={[styles.tableRow, { backgroundColor: 'lightgray' }]} fixed>
        <View style={[styles.firstCol, { borderLeft: 1, borderRight: 0}]}>
          <Text style={[styles.tableCell, styles.header]}>#</Text>
        </View>
        <View style={[styles.descCol, { borderRight: 0 }]}>
          <Text style={[styles.tableCell, styles.header, { textAlign: 'left' }]}>
            Description
          </Text>
        </View>
        <View style={[styles.tableCol, { borderRight: 0 }]}>
          <Text style={[styles.tableCell, styles.header, { textAlign: 'left' }]}>Qty</Text>
        </View>
        <View style={[styles.tableCol, { borderRight: 0 }]}>
          <Text style={[styles.tableCell, styles.header, { textAlign: 'left' }]}>
            Unit
          </Text>
        </View>
        <View style={[styles.hnsCol, { borderRight: 0 }]}>
          <Text style={[styles.tableCell, styles.header, { textAlign: 'left' }]}>HSN/SAC</Text>
        </View>
        {showAmount && (
          <>
            <View style={[styles.tableCol, { borderRight: 0 }]}>
              <Text style={[styles.tableCell, styles.header, { textAlign: 'left' }]}>Rate</Text>
            </View>
            <View style={[styles.tableCol, { borderRight: 0 }]}>
              <Text style={[styles.tableCell, styles.header, { textAlign: 'left' }]}>Tax %</Text>
            </View>
            <View style={[styles.tableCol, { borderRight:1}]}>
              <Text style={[styles.tableCell, styles.header, { textAlign: 'left' }]}>
                Amount
              </Text>
            </View>
          </>
        )}
      </View>

      {/* Table Rows */}
      {data.map((item, index) => (
        <View style={styles.tableRow} key={index} wrap={false}>
          <View style={[styles.firstCol, { borderLeft: 0, borderTop: 0 }]}>
            <Text style={[styles.tableCell, { textAlign: 'left' }]}>{index + 1}</Text>
          </View>
          <View style={[styles.descCol, { borderLeft: 0, borderTop: 0 }]}>
            <Text
              style={[
                styles.tableCell,
                { textAlign: 'left', whiteSpace: 'pre-wrap', overflowWrap: 'break-word' },
              ]}
            >
              {item?.alternateItemTitle || item.itemId.title}
            </Text>
          </View>
          <View style={[styles.tableCol, { borderLeft: 0, borderTop: 0 }]}>
            <Text style={[styles.tableCell, { textAlign: 'left' }]}>
              {item.quantity}
            </Text>
          </View>
          <View style={[styles.tableCol, { borderLeft: 0, borderTop: 0 }]}>
            <Text style={[styles.tableCell, { textAlign: 'left' }]}>
              {item.itemUnit || item.itemId.itemUnit}
            </Text>
          </View>
          <View style={[styles.hnsCol, { borderLeft: 0, borderTop: 0, }]}>
            <Text style={[styles.tableCell, { textAlign: 'right' }]}>
              {item.hsnCode || item.itemId.hsnCode}
            </Text>
          </View>
          {showAmount && (
            <>
              <View style={[styles.tableCol, { borderLeft: 0, borderTop: 0, }]}>
                <Text style={[styles.tableCell, { textAlign: 'right' }]}>
                  {item.unitPrice}
                </Text>
              </View>
              <View style={[styles.tableCol, { borderLeft: 0, borderTop: 0, }]}>
                <Text style={[styles.tableCell, { textAlign: 'right' }]}>
                  {item.taxes.reduce((res, tax) => res + tax.rate, 0)}
                </Text>
              </View>
              <View style={[styles.tableCol, { borderRight: 0, borderLeft: 0, borderTop: 0 }]}>
                <Text style={[styles.tableCell, { textAlign: 'right' }]}>
                  {item.unitPrice * item.quantity}
                </Text>
              </View>
            </>
          )}
        </View>
      ))}
    </View>
  );
};

const getStatusLabel = (status, docType) => {
  if (!status) return "";

  const statusLower = status.toLowerCase().trim();

  if (docType === "PR") {
    switch (statusLower) {
      case 'draft':
        return 'Unsent';
      case 'published':
        return 'Sent';
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      default:
        return status;
    }
  }

  if (docType === "PO") {
    switch (statusLower) {
      case 'draft':
        return 'Unsent';
      case 'sent':
        return 'Sent';
      case 'accepted':
        return 'Accepted';
      case 'declined':
        return 'Declined';
      case 'approved':
        return 'Approved';
      default:
        return status;
    }
  }

  return status;
};

// Define the PDF document structure
const PdfDocument = ({ data, showAmount, status, docType }) => {
  const resolvedDocType = docType || (data.orderInfo.POStatus ? "PO" : "PR");
  const currentStatus = getStatusLabel(status || data.orderInfo.POStatus, resolvedDocType);

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        {/* Watermark */}
        {currentStatus && (
          <View style={styles.watermarkWrapper} fixed>
            <View style={styles.watermarkContainer}>
              <Text style={styles.watermarkText}>
                {currentStatus.toUpperCase()}
              </Text>
            </View>
          </View>
        )}

        <View style={styles.pageBorder}fixed/>
          <View style={[styles.section, { paddingVertical: 10, paddingHorizontal: 6 }]}>
            <View style={styles.address}>
              <Text style={styles.header}>{data.orderInfo.clientName}</Text>
              <Text style={styles.info}>{data.orderInfo.billing?.branchName}</Text>
              <Text style={styles.info}>{getAddressValue(data.orderInfo.billing)}</Text>
              <Text style={styles.info}>{data.orderInfo.billing?.contact}</Text>
              <Text style={[styles.info]}>GST No.: {data.orderInfo.clientGst}</Text>
            </View>
            <View style={styles.address}>
              <Text style={[styles.title, { textAlign: 'right' }]}>
                {resolvedDocType === "PO" ? "PURCHASE ORDER" : "PURCHASE REQUISITION"}
              </Text>
              <View style={[styles.section, { justifyContent: 'flex-end' }]}>
                {!!currentStatus && <Text style={styles.status}>{currentStatus}</Text>}
              </View>
            </View>
          </View>

          {/* Divider */}
          <View style={styles.divider} />

          <View style={{ paddingHorizontal: 10 }}>
            <View style={styles.section}>
              <Text style={[styles.address, styles.info]}>{data.orderInfo.purchaseOrderNumber ? `Purchase Order: ${data.orderInfo.purchaseOrderNumber}` : `Purchase Requisition: ${data.orderInfo.purchaseRequisitionNumber}`}</Text>
              <Text style={[styles.address, styles.info]}>Date: {formatDate(data.orderInfo.createdAt)}</Text>
            </View>
          </View>

          <View style={[styles.tableRow, { backgroundColor: 'lightgray' }]}>
            <View style={[styles.tableCol, { borderLeft: 0, borderRight: 0 }]}>
              <Text style={[styles.tableCell, styles.header, { textAlign: 'left', }]}>Vendor Address:</Text>
            </View>
            <View style={[styles.tableCol, { borderRight: 0 }]}>
              <Text style={[styles.tableCell, styles.header, { textAlign: 'left' }]}>
                Deliver To:
              </Text>
            </View>
          </View>
          <View style={styles.tableRow}>
            <View style={[styles.tableCol, { paddingVertical: 5, borderLeft: 0, borderRight: 0, borderTop: 0 }]}>
              <Text style={[styles.tableCell, styles.info, { textAlign: 'left' }]}>Bansi Office Solutions Private Limited</Text>
              <Text style={[styles.tableCell, styles.info, { textAlign: 'left' }]}>{getOfficeAddress(data?.orderInfo?.billing)}</Text>
              <Text style={[styles.tableCell, styles.info, { textAlign: 'left' }]}>Web: www.willsmeet.com</Text>
              <Text style={[styles.tableCell, styles.info, { textAlign: 'left' }]}>Email:{data?.orderInfo?.billing?.associatedWarehouse?.email||"sales@willsmeet.com"}</Text>
            </View>
            <View style={[styles.tableCol, { paddingVertical: 5, borderRight: 0, borderTop: 0 }]}>
              <Text style={[styles.tableCell, styles.info, { textAlign: 'left' }]}>{data.orderInfo.shipping?.branchName}</Text>
              <Text style={[styles.tableCell, styles.info, { textAlign: 'left' }]}>{getAddressValue(data.orderInfo.shipping)}</Text>
              <Text style={[styles.tableCell, styles.info, { textAlign: 'left' }]}>{data.orderInfo.shipping?.contact}</Text>
            </View>
          </View>

          <View style={styles.table}>
            <Text style={[styles.header, { padding: 5 }]}>Sales Information</Text>

            <TableWithPagination data={[...data.itemsData,]} showAmount={showAmount} />

            {/* Terms & Conditions and Total Section */}
            <View style={[styles.tableRow, { minHeight: 200, }]}>
              <View style={[styles.terms, { borderRight: 0 }]}>
                <Text style={[styles.tableCell, { color: "grey", textAlign: 'left', width: "80%", paddingTop: 20 }]}>Terms & Conditions</Text>
                <Text style={[styles.tableCell, { textAlign: 'left', width: "80%" }]}>1.All the invoices should mandatorily be mentioned with Bansi Office
                  Solutions Private Limited PO Number & should be attached with PO copy
                  ,without which the invoice would be considered invalid.
                </Text>
                <Text style={[styles.tableCell, { textAlign: 'left', width: "80%" }]}>
                  2.All the invoices should be mentioned with vendor bank details.
                </Text>
              </View>
              <View style={{ width: '40%', }}>
                {showAmount && (
                  <>
                      <View style={[styles.tableCol, styles.section, { height: 80, marginBottom: 0, borderRight: 0, borderTop: 0 }]}>
                        <View style={styles.address}>
                          {getTaxRows(data).map((row, i) => (
                            <Text key={row.label} style={[styles.tableCell, { textAlign: 'right', paddingTop: 3 }]}>
                              {row.label}
                            </Text>
                          ))}
                        </View>
                        <View style={styles.address}>
                          {getTaxRows(data).map((row, i) => (
                            <Text key={row.label} style={[styles.tableCell, { textAlign: 'right', paddingTop: 3 }]}>
                              {parseFloat(row.value).toFixed(2)}
                            </Text>
                          ))}
                        </View>
                      </View>
                  </>
                )}
                <View style={[styles.tableCol, styles.section, { height: 30, marginBottom: 20, borderTop: 0, borderRight: 0 }]}>
                  <View>
                    <Text style={styles.note}>{resolvedDocType === "PO" ? "***This is a system-generated PO, no signature required.***" : "***This is a system-generated PR, no signature required.***"}</Text>
                  </View>
                </View>
                <View style={{ marginTop: 10 }}>
                  <View style={{ flexDirection: "row", marginBottom: 4 }}>
                    <Text style={{ width: 90, fontWeight: "bold" }}>Created By:</Text>
                    <Text>
                      {data?.orderInfo?.createdBy?.firstName}{" "}
                      {data?.orderInfo?.createdBy?.lastName}
                    </Text>
                  </View>

                  <View style={{ flexDirection: "row", marginBottom: 4 }}>
                    <Text style={{ width: 90, fontWeight: "bold" }}>Approved By:</Text>
                    <Text>
                      {data?.orderInfo?.approvalHistory?.[0]?.approvedBy?.firstName}{" "}
                      {data?.orderInfo?.approvalHistory?.[0]?.approvedBy?.lastName}
                    </Text>
                  </View>

                  <View style={{ flexDirection: "row" }}>
                    <Text style={{ width: 90, fontWeight: "bold" }}>Approved Date:</Text>
                    <Text>
                    <>
                    {data?.orderInfo?.approvalHistory?.[0]?.approvedAt ? (
                        formatDate(data?.orderInfo?.approvalHistory?.[0]?.approvedAt)
                      ) : ''}
                    </>
                    </Text>
                  </View>
                </View>
              </View>
            </View>
          </View>
      
        <Text
          fixed
          style={{
            position: 'absolute',
            bottom: 15,
            left: 0,
            right: 30,
            fontSize: 8,
            textAlign: 'right',
          }}
          render={({ pageNumber, totalPages }) =>
            totalPages > 1 ? `${pageNumber}` : ''
          }
        />
      </Page>
    </Document>
  )
};

const PdfComponent = ({ data, status, docType }) => {
  const { auth } = useAuth();
  const showAmount = auth.permissions.find(ele => ele.module === MODULES_ENUM.ORDERS)?.operations.includes(PERMISSIONS_ENUM.CREATE) || false;
  const handleOpenPdf = async () => {
    const blob = await pdf(
      <PdfDocument
        data={data}
        showAmount={showAmount}
        status={status}
        docType={docType}
      />
    ).toBlob();
    const url = URL.createObjectURL(blob);

    window.open(url, '_blank');
  };

  return (
    <div>
      {/* <PDFDownloadLink document={<PdfDocument data={data} />} fileName={`${data.orderInfo.purchaseOrderNumber}.pdf`}>
        {({ blob, url, loading, error }) =>
          <Button color="primary" outline onClick={handleOpenPdf}>{loading ? 'Loading...' : 'Download PDF'}</Button>
        }
      </PDFDownloadLink> */}
      <Button style={{
        width: "124px",
      }} color="primary" outline onClick={handleOpenPdf}>Download PDF</Button>
    </div>
  );
};

export default PdfComponent;
